import { defineMessages } from '@alltrails/shared/react-intl';
import Modal from 'components/Modal';
import Typography from '@alltrails/shared/denali/components/Typography';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import AppStore from '@alltrails/shared/components/storeBadges/AppStore';
import GooglePlay from '@alltrails/shared/components/storeBadges/GooglePlay';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import React from 'react';
import classNames from 'classnames';
import appIconSrc from '../../../assets/images/icons/app_icon.png';
import * as styles from './styles/styles.module.scss';

const ALT_TEXT_STRINGS = defineMessages({
  LOGO_ALT_TEXT: {
    defaultMessage: 'The AllTrails mobile app icon'
  },
  QR_CODE_ALT_TEXT: {
    defaultMessage: 'A QR code that links to the AllTrails mobile app store listing'
  },
  SUBTITLE_MOBILE: {
    defaultMessage: 'Available now for iOS or Android.'
  },
  SUBTITLE_DESKTOP: {
    defaultMessage: 'Scan the QR code to download the app.'
  }
});

type MobileFeatureModalProps = {
  className?: string;
  isOpen: boolean;
  qrCodeImgSrc: string;
  title: React.ReactNode;
  subtitleMobile?: React.ReactNode;
  subtitleDesktop?: React.ReactNode;
  onRequestClose: () => void;
  variant?: 'navigator' | 'explore';
};

function MobileFeatureModal({
  className,
  isOpen,
  qrCodeImgSrc,
  title,
  subtitleMobile,
  subtitleDesktop,
  onRequestClose,
  variant = 'navigator'
}: MobileFeatureModalProps) {
  const languageRegionCode = useLanguageRegionCode();

  const {
    formattedDefaultMessages: { LOGO_ALT_TEXT, QR_CODE_ALT_TEXT, SUBTITLE_MOBILE, SUBTITLE_DESKTOP }
  } = useFormatMessage(ALT_TEXT_STRINGS);

  const headingVariant = variant === 'navigator' ? 'heading500' : 'heading400';
  const subtitleColor = variant === 'navigator' ? undefined : 'secondary';
  const subtitleVariant = variant === 'navigator' ? 'paragraph100' : 'text200';

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose}>
      <div className={classNames(styles.container, className)}>
        <img src={qrCodeImgSrc} alt={QR_CODE_ALT_TEXT} className={styles.qrCode} />
        <img src={appIconSrc} alt={LOGO_ALT_TEXT} className={styles.appIcon} />
        <Typography className={classNames(styles.title, variant === 'explore' && styles.exploreTitle)} component="h2" variant={headingVariant}>
          {title}
        </Typography>
        <Typography color={subtitleColor} component="p" variant={subtitleVariant}>
          {subtitleMobile || SUBTITLE_MOBILE}
          <span className={styles.desktopText}> {subtitleDesktop || SUBTITLE_DESKTOP}</span>
        </Typography>
        <div className={styles.mobileStoreButtons}>
          <AppStore height={40} languageRegionCode={languageRegionCode} />
          <GooglePlay height={40} languageRegionCode={languageRegionCode} />
        </div>
      </div>
    </Modal>
  );
}

export default MobileFeatureModal;
